// Ayarlar → Entegrasyonlar: Jira bağlantısı (müşteri bazlı, SALT OKUNUR). Diğer platform kartlarıyla aynı kalıp.
// Asistan, bağlıysa Jira issue'larını okuyup yorumlar. Backend: /ai-advisor/jira/* (jira_router.py)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { Button } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'Bağlı': 'Connected',
  'Bağlı Değil': 'Not connected',
  'Jira adresi': 'Jira site',
  'E-posta': 'Email',
  'API token': 'API token',
  'Asistan, Jira issue’larını salt okunur okuyup işlerini yorumlayabilir. Yalnızca müşteri yöneticisi bağlayabilir.': 'The assistant can read your Jira issues (read-only) and interpret your work. Only the client admin can connect.',
  'Token: id.atlassian.com → Security → API tokens. Şifreli saklanır, yalnızca okuma için kullanılır.': 'Token: id.atlassian.com → Security → API tokens. Stored encrypted, used for read access only.',
  'Bağlan': 'Connect',
  'Bağlantıyı Kes': 'Disconnect',
  'Bağlanıyor…': 'Connecting…',
  'Jira bağlantısını kesmek istediğine emin misin?': 'Disconnect Jira?',
});

const API = '/api/v1/ai-advisor/jira';

const label: React.CSSProperties = { fontSize: 9, fontWeight: 700, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif', marginBottom: 6 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${T.borderMid}`, fontSize: 12, fontFamily: 'Manrope, sans-serif', background: T.subtleBg, outline: 'none', color: T.text, boxSizing: 'border-box', marginBottom: 12 };

export default function JiraIntegrationCard({ clientId, embedded, onStatus }: { clientId: number; embedded?: boolean; onStatus?: (connected: boolean) => void }) {
  const { t } = useLang();
  const [status, setStatus] = useState<{ connected: boolean; site?: string; email?: string } | null>(null);
  const [site, setSite] = useState(''); const [email, setEmail] = useState(''); const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');

  const load = () => axios.get(`${API}/status?client_id=${clientId}`).then(r => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/connect`, { client_id: clientId, site, email, api_token: token }); setToken(''); await load(); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Bağlantı kurulamadı.'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(t('Jira bağlantısını kesmek istediğine emin misin?'))) return;
    await axios.post(`${API}/disconnect`, { client_id: clientId }).catch(() => {});
    load();
  };
  const on = !!status?.connected;
  useEffect(() => { if (status) onStatus?.(!!status.connected); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={embedded ? undefined : { background: T.surface, borderRadius: 14, border: `1px solid ${T.border}`, padding: '20px 22px' }}>
      {!embedded && (<>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: T.trackBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/jira-icon.svg" width={22} height={22} style={{ objectFit: 'contain' }} alt="Jira" />
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
            background: on ? T.greenTint : T.trackBg, color: on ? T.greenText : T.textMidAlt }}>{on ? t('Bağlı') : t('Bağlı Değil')}</span>
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: 'Manrope, sans-serif', marginBottom: 4 }}>Jira</div>
      </>)}
      {on ? (
        <div>
          <div style={{ fontSize: 11, color: T.greenText, fontWeight: 600, marginBottom: 4 }}><span style={{ fontFamily: MONO }}>{status?.site}</span></div>
          <div style={{ fontSize: 11, color: T.textMidAlt, marginBottom: 10 }}>{status?.email}</div>
          <button onClick={disconnect} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: `1px solid ${T.redTint}`, background: T.redTint, color: T.redText, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
            {t('Bağlantıyı Kes')}
          </button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.5, marginBottom: 12 }}>{t('Asistan, Jira issue’larını salt okunur okuyup işlerini yorumlayabilir. Yalnızca müşteri yöneticisi bağlayabilir.')}</div>
          <div style={label}>{t('Jira adresi')}</div>
          <input style={input} value={site} onChange={e => setSite(e.target.value)} placeholder="sirket.atlassian.net" />
          <div style={label}>{t('E-posta')}</div>
          <input style={input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kullanici@firma.com" autoComplete="off" />
          <div style={label}>{t('API token')}</div>
          <input style={input} type="password" value={token} onChange={e => setToken(e.target.value)} placeholder="Atlassian API token" autoComplete="new-password" />
          {err && <div style={{ fontSize: 11, color: T.redText, marginBottom: 8 }}>{err}</div>}
          <Button variant="solid" disabled={busy || !site || !email || !token} onClick={connect} style={{ width: '100%' }}>{busy ? t('Bağlanıyor…') : t('Bağlan')}</Button>
          <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 8 }}>{t('Token: id.atlassian.com → Security → API tokens. Şifreli saklanır, yalnızca okuma için kullanılır.')}</div>
        </>
      )}
    </div>
  );
}
